// frontend/src/services/authService.js - Authentication API service

import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.focusview.ai';

class AuthService {
  // Token management
  setToken(token) {
    localStorage.setItem('auth_token', token);
  }

  getToken() {
    return localStorage.getItem('auth_token');
  }

  removeToken() {
    localStorage.removeItem('auth_token');
  }

  isAuthenticated() {
    return !!this.getToken();
  }

  // Get current user from localStorage (SYNCHRONOUS - DO NOT MAKE THIS ASYNC!)
  getCurrentUser() {
    const u = localStorage.getItem('current_user');
    return u ? JSON.parse(u) : null;
  }

  // Set authorization header
  getAuthHeader() {
    const token = this.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  // Check if email exists
  async checkEmail(email) {
    try {
      const response = await axios.get(`${API_URL}/auth/check-email`, {
        params: { email }
      });
      return response.data.exists;
    } catch (error) {
      console.error('Error checking email:', error);
      throw error;
    }
  }

  // Register new user - Send placeholder if no wallet provided
  async register(email, password, dogeWallet = null, turnstileToken = '') {
    try {
      // Wallet is set later at withdrawal time — send null/empty so DB stays clean
      const walletToSend = dogeWallet && dogeWallet.trim() ? dogeWallet.trim() : null;

      const response = await axios.post(`${API_URL}/auth/register`, {
        email,
        password,
        dogecoin_wallet: walletToSend,
        turnstile_token: turnstileToken
      });

      const { access_token, user } = response.data;
      this.setToken(access_token);
      localStorage.setItem('current_user', JSON.stringify(user));
      return { user, token: access_token };
    } catch (error) {
      console.error('Registration error:', error);
      if (error.response?.data?.detail) {
        throw error.response.data.detail;
      } else if (error.response?.data?.message) {
        throw error.response.data.message;
      } else if (error.message) {
        throw error.message;
      }
      throw 'Registration failed. Please try again.';
    }
  }

  // Login
  async login(email, password) {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        email,
        password
      });

      const { access_token, user } = response.data;
      this.setToken(access_token);
      localStorage.setItem('current_user', JSON.stringify(user));
      return { user, token: access_token };
    } catch (error) {
      console.error('Login error:', error);
      if (error.response?.data?.detail) {
        throw error.response.data.detail;
      } else if (error.response?.data?.message) {
        throw error.response.data.message;
      } else if (error.message) {
        throw error.message;
      }
      throw 'Login failed. Please try again.';
    }
  }

  // Logout
  logout() {
    this.removeToken();
    localStorage.removeItem('current_user');
    window.location.href = '/';
  }

  // Delete account permanently
  async deleteAccount() {
    try {
      await axios.delete(`${API_URL}/auth/delete-account`, {
        headers: this.getAuthHeader()
      });
      this.removeToken();
      localStorage.removeItem('current_user');
      return { success: true };
    } catch (error) {
      throw error?.response?.data?.detail || 'Failed to delete account. Please try again.';
    }
  }

  // RENAMED: Fetch fresh user data from API (use when you need to refresh user data)
  async fetchUserFromAPI() {
    try {
      const response = await axios.get(`${API_URL}/auth/me`, {
        headers: this.getAuthHeader()
      });
      // Update localStorage with fresh data
      localStorage.setItem('current_user', JSON.stringify(response.data));
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        this.removeToken();
      }
      throw error;
    }
  }

  // Confirm email
  async confirmEmail(token) {
    try {
      const response = await axios.get(`${API_URL}/auth/confirm`, {
        params: { token }
      });

      if (response.data.access_token) {
        this.setToken(response.data.access_token);
      }

      return response.data;
    } catch (error) {
      throw error.response?.data?.detail || 'Email confirmation failed';
    }
  }

  // Add wallet
  async addWallet(dogeWallet) {
    try {
      const response = await axios.post(
        `${API_URL}/user/wallet/add`,
        { dogecoin_wallet: dogeWallet },
        { headers: this.getAuthHeader() }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data?.detail || 'Failed to add wallet';
    }
  }

  // Get wallet status
  async getWalletStatus() {
    try {
      const response = await axios.get(`${API_URL}/user/wallet/status`, {
        headers: this.getAuthHeader()
      });
      return response.data;
    } catch (error) {
      throw error.response?.data?.detail || 'Failed to get wallet status';
    }
  }

  // Get remaining wallet slots (public)
  async getRemainingSlots() {
    try {
      const response = await axios.get(`${API_URL}/user/wallet/remaining`);
      return response.data;
    } catch (error) {
      console.error('Error getting remaining slots:', error);
      return { slots_remaining: 0, total_slots: 100 };
    }
  }

  // Track article read and earn reward
  async trackArticleRead(articleId) {
    try {
      const response = await axios.post(
        `${API_URL}/rewards/article-read`,
        { article_id: articleId },
        { headers: this.getAuthHeader() }
      );
      console.log('trackArticleRead API response:', response.data);
      return response.data;
    } catch (error) {
      // Silent fail if not authenticated
      if (error.response?.status !== 401) {
        console.error('Error tracking article:', error);
      }
      return null;
    }
  }

  // Get reward balance
  async getBalance() {
    try {
      const response = await axios.get(`${API_URL}/rewards/balance`, {
        headers: this.getAuthHeader()
      });
      return response.data;
    } catch (error) {
      throw error.response?.data?.detail || 'Failed to get balance';
    }
  }

  // Get reward stats
  async getRewardStats() {
    try {
      const response = await axios.get(`${API_URL}/rewards/stats`, {
        headers: this.getAuthHeader()
      });
      return response.data;
    } catch (error) {
      throw error.response?.data?.detail || 'Failed to get stats';
    }
  }

  // Get reward history
  async getRewardHistory(limit = 50, offset = 0) {
    try {
      const response = await axios.get(`${API_URL}/rewards/history`, {
        params: { limit, offset },
        headers: this.getAuthHeader()
      });
      return response.data;
    } catch (error) {
      throw error.response?.data?.detail || 'Failed to get history';
    }
  }

  // Get newsletter subscription status
  async getNewsletterStatus() {
    try {
      const response = await axios.get(`${API_URL}/auth/newsletter-status`, {
        headers: this.getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error('Newsletter status error:', error);
      return { newsletter_subscribed: false };
    }
  }

  // Re-subscribe to newsletter (no reward given)
  async resubscribeNewsletter() {
    try {
      const response = await axios.post(
        `${API_URL}/auth/resubscribe-newsletter`,
        {},
        { headers: this.getAuthHeader() }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data?.detail || 'Failed to resubscribe';
    }
  }

  // Send password reset code (6-digit code valid for 30 min)
  async sendPasswordResetCode(email) {
    try {
      const response = await axios.post(`${API_URL}/auth/forgot-password`, {
        email
      });
      return response.data;
    } catch (error) {
      console.error('Send reset code error:', error);
      if (error.response?.data?.detail) {
        throw error.response.data.detail;
      }
      throw 'Failed to send reset code. Please try again.';
    }
  }

  // Reset password with code and auto-login
  async resetPasswordWithCode(email, code, newPassword) {
    try {
      const response = await axios.post(`${API_URL}/auth/reset-password`, {
        email,
        code,
        new_password: newPassword
      });

      const { access_token, user } = response.data;
      this.setToken(access_token);
      localStorage.setItem('current_user', JSON.stringify(user));
      return { user, token: access_token };
    } catch (error) {
      console.error('Reset password error:', error);
      if (error.response?.data?.detail) {
        throw error.response.data.detail;
      }
      throw 'Failed to reset password. Please check your code.';
    }
  }

  // Verify 6-digit OTP code
  async verifyCode(email, code) {
    try {
      const response = await axios.post(`${API_URL}/auth/verify-code`, { email, code });
      if (response.data.access_token) {
        this.setToken(response.data.access_token);
        await this.fetchUserFromAPI();
      }
      return response.data;
    } catch (error) {
      if (error.response?.data?.detail) throw error.response.data.detail;
      throw 'Verification failed. Please try again.';
    }
  }

  // Resend OTP code
  async resendCode(email) {
    try {
      const response = await axios.post(`${API_URL}/auth/resend-code`, { email });
      return response.data;
    } catch (error) {
      if (error.response?.data?.detail) throw error.response.data.detail;
      throw 'Failed to resend code. Please try again.';
    }
  }
}

export default new AuthService();